import useAudience from './audiences'

import { AUDIENCE_TEMPLATES_CONFIG, type AudienceTemplate } from '@/views/audience/config/templates.config'
import type { Template } from '@/views/autopilot/config/templates.config'
import type { Audience, AudienceRule } from '@/types/types'

export type CreationStep = 'creating' | 'completed'

export const useMarketingFlowWithAudience = () => {
  const { createAudience } = useAudience()

  const currentCreationStep = ref<CreationStep | null>(null)
  const isCreatingWithAudience = ref(false)
  const creationError = ref<string | null>(null)

  const getAudienceTemplateConfig = (templateType: AudienceTemplate['type']) => {
    return AUDIENCE_TEMPLATES_CONFIG.find((template) => template.type === templateType)
  }

  const createAudienceFromTemplate = async (
    audienceTemplateType: AudienceTemplate['type'],
    marketingFlowName: string
  ): Promise<number | undefined> => {
    const audienceTemplate = getAudienceTemplateConfig(audienceTemplateType)
    if (!audienceTemplate) {
      throw new Error(`Audience template ${audienceTemplateType} not found`)
    }

    const audienceName = marketingFlowName

    const audience: Audience = {
      name: audienceName,
      type: 'dynamic',
      template_used: audienceTemplateType
    }

    const rules: Record<string, AudienceRule> = {}
    Object.entries(audienceTemplate.rules).forEach(([key, value]) => {
      rules[key] = value as AudienceRule
    })

    try {
      const audienceId = await createAudience({
        audience,
        rules,
        sendNotification: false
      })

      return audienceId
    } catch (error) {
      throw new Error(`Failed to create audience: ${error.message}`)
    }
  }

  const createMarketingFlowWithAudience = async (
    template: Template,
    marketingFlowName: string
  ): Promise<{ audienceId?: number; success: boolean }> => {
    if (!template.requiresAudienceTemplate) {
      return { success: false }
    }

    try {
      isCreatingWithAudience.value = true
      creationError.value = null

      await new Promise((resolve) => setTimeout(resolve, 300))

      currentCreationStep.value = 'creating'
      const audienceId = await createAudienceFromTemplate(template.requiresAudienceTemplate, marketingFlowName)

      if (!audienceId) {
        throw new Error('Failed to create audience')
      }

      currentCreationStep.value = 'completed'

      await new Promise((resolve) => setTimeout(resolve, 2000))

      return { audienceId, success: true }
    } catch (error) {
      creationError.value = error.message
      return { success: false }
    } finally {
      // Don't reset isCreatingWithAudience here, let the component handle it
    }
  }

  const resetCreationState = () => {
    currentCreationStep.value = null
    isCreatingWithAudience.value = false
    creationError.value = null
  }

  return {
    currentCreationStep,
    isCreatingWithAudience,
    creationError,
    getAudienceTemplateConfig,
    createAudienceFromTemplate,
    createMarketingFlowWithAudience,
    resetCreationState
  }
}
